import { LoadSavedMap } from "./main";
import { Tile } from "./map";

const EXPECTED_ROWS = 8;
const EXPECTED_COLUMNS = 7;
const REVEAL_HISTORY_KEY = 'scratchRevealHistory';

export type RevealArea = 'mine' | 'forest';

export interface RevealHistoryEntry {
    area: RevealArea;
    x: number;
    y: number;
}

export class GameData {
    mine: Tile[][] = [];
    forest: Tile[][] = [];
    revealHistory: RevealHistoryEntry[] = [];
  
    constructor() {
    }
  
    // Method to save data to localStorage
    saveData(mineData: Tile[][], forestData: Tile[][], revealHistory: RevealHistoryEntry[] = []): void {
      localStorage.setItem('mine', JSON.stringify(mineData));
      localStorage.setItem('forest', JSON.stringify(forestData));
      localStorage.setItem(REVEAL_HISTORY_KEY, JSON.stringify(revealHistory));
    }
  
    // Method to load data from localStorage
    loadData(): boolean {
      const savedMine = localStorage.getItem('mine');
      const savedForest = localStorage.getItem('forest');

      if (savedMine == null || savedForest == null) {
        return false;
      }

      try {
        const mine = this.hydrateMap(JSON.parse(savedMine));
        const forest = this.hydrateMap(JSON.parse(savedForest));
        const savedHistory = localStorage.getItem(REVEAL_HISTORY_KEY);

        if (mine == null || forest == null) {
          return false;
        }

        this.mine = mine;
        this.forest = forest;
        this.revealHistory = savedHistory == null
          ? this.createHistoryFromDiscovered(mine, forest)
          : this.hydrateRevealHistory(JSON.parse(savedHistory), mine, forest) ?? this.createHistoryFromDiscovered(mine, forest);
        return true;
      } catch {
        return false;
      }
    }
  
    // Method to clear saved data
    clearData(): void {
      localStorage.removeItem('mine');
      localStorage.removeItem('forest');
      localStorage.removeItem(REVEAL_HISTORY_KEY);
    }
  
    // Check if there is saved data and ask the player if they want to use it
    async checkForSavedData(): Promise<boolean> {
      const savedMine = localStorage.getItem('mine');
      const savedForest = localStorage.getItem('forest');
    
      if (savedMine && savedForest) {
        const useSavedData = await this.confirmPopup("Saved data found. Do you want to use this data?");
        if (useSavedData && this.loadData()) {
          LoadSavedMap();
          return true;
        }
      }

      if (savedMine || savedForest) {
        this.clearData();
      }

      return false;
    }

    private hydrateMap(value: unknown): Tile[][] | null {
      if (!Array.isArray(value) || value.length !== EXPECTED_ROWS) {
        return null;
      }

      const hydrated = value.map((row, y) => {
        if (!Array.isArray(row) || row.length !== EXPECTED_COLUMNS) {
          return null;
        }

        return row.map((tile, x) => Tile.fromSaved(tile, x, y));
      });

      if (hydrated.some((row) => row == null || row.some((tile) => tile == null))) {
        return null;
      }

      return hydrated as Tile[][];
    }

    private hydrateRevealHistory(value: unknown, mine: Tile[][], forest: Tile[][]): RevealHistoryEntry[] | null {
      if (!Array.isArray(value)) {
        return null;
      }

      const seenTiles = new Set<string>();
      const history: RevealHistoryEntry[] = [];

      for (const entry of value) {
        if (!isRecord(entry) || !isRevealArea(entry.area) ||
          !isValidCoordinate(entry.x, EXPECTED_COLUMNS) ||
          !isValidCoordinate(entry.y, EXPECTED_ROWS)) {
          return null;
        }

        const revealEntry: RevealHistoryEntry = {
          area: entry.area,
          x: entry.x,
          y: entry.y,
        };
        const tile = this.getTileForRevealEntry(revealEntry, mine, forest);
        const tileKey = this.getRevealEntryKey(revealEntry);

        if (tile?.discovered === true && !seenTiles.has(tileKey)) {
          seenTiles.add(tileKey);
          history.push(revealEntry);
        }
      }

      return history;
    }

    private createHistoryFromDiscovered(mine: Tile[][], forest: Tile[][]): RevealHistoryEntry[] {
      return [
        ...this.getDiscoveredEntries('forest', forest),
        ...this.getDiscoveredEntries('mine', mine),
      ];
    }

    private getDiscoveredEntries(area: RevealArea, tileSet: Tile[][]): RevealHistoryEntry[] {
      const entries: RevealHistoryEntry[] = [];

      tileSet.forEach((row, y) => {
        row.forEach((tile, x) => {
          if (tile.discovered) {
            entries.push({ area, x, y });
          }
        });
      });

      return entries;
    }

    private getTileForRevealEntry(entry: RevealHistoryEntry, mine: Tile[][], forest: Tile[][]): Tile | undefined {
      const tileSet = entry.area === 'mine' ? mine : forest;
      return tileSet[entry.y]?.[entry.x];
    }

    private getRevealEntryKey(entry: RevealHistoryEntry): string {
      return `${entry.area}:${entry.x}:${entry.y}`;
    }
    
    confirmPopup(message: string): Promise<boolean> {
      return new Promise((resolve) => {
        const confirmed = confirm(message);
        resolve(confirmed);
      });
    }
  }

function isRevealArea(value: unknown): value is RevealArea {
  return value === 'mine' || value === 'forest';
}

function isValidCoordinate(value: unknown, maxExclusive: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < maxExclusive;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
